import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const Membership: React.FC = () => {
  const { t } = useTranslation();
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'intro' | 'register' | 'success'>('intro');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [company, setCompany] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);

  if (isAuthenticated) {
    navigate('/quote');
    return null;
  }

  const handleSocialLogin = async (provider: 'google' | 'github') => {
    try {
      await login(provider);
    } catch (err) {
      setError('Social login failed. Please try again.');
    }
  };

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName || !email || !password) {
      setError('Please fill in all required fields.');
      return;
    }
    setRegistering(true);
    try {
      await register(email, password, fullName);
      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  return (
    <main className="membership-page">
      <nav className="membership-nav">
        <Link to="/" className="membership-brand">{t('home.brand')}</Link>
        <div className="membership-nav-links">
          <Link to="/" className="membership-nav-link">{t('common.home')}</Link>
          <Link to="/quote" className="membership-nav-link">{t('home.hero.cta1')}</Link>
        </div>
      </nav>

      <div className="membership-container">
        {step === 'intro' && (
          <>
            <section className="membership-hero">
              <h1>{t('membership.title')}</h1>
              <p className="membership-subtitle">{t('membership.subtitle')}</p>
              <button
                className="membership-cta-btn"
                onClick={() => setStep('register')}
              >
                {t('home.hero.cta2')} →
              </button>
            </section>

            <section className="membership-why">
              <h2>{t('membership.whyTitle')}</h2>
              <div className="membership-why-grid">
                {(t('membership.whyItems', { returnObjects: true }) as { title: string; desc: string }[]).map((item, i) => (
                  <div key={i} className="membership-why-card">
                    <div className="membership-why-icon">
                      {i === 0 && '💰'}
                      {i === 1 && '⚡'}
                      {i === 2 && '🌍'}
                      {i === 3 && '🔒'}
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="membership-cta-bottom">
              <p>{t('membership.registerSubtitle')}</p>
              <button
                className="membership-cta-btn"
                onClick={() => setStep('register')}
              >
                {t('home.hero.cta2')} →
              </button>
            </section>

            <section className="membership-partner">
              <p>{t('membership.partnerCta')}</p>
              <Link to="/register" className="membership-partner-link">
                {t('membership.partnerLink')} →
              </Link>
            </section>
          </>
        )}

        {step === 'register' && (
          <section className="membership-form-section">
            <div className="membership-form-card">
              <h2>{t('membership.registerTitle')}</h2>
              <p className="membership-form-subtitle">{t('membership.registerSubtitle')}</p>

              {error && (
                <div className="membership-error">
                  {error}
                </div>
              )}

              <div className="membership-social-btns">
                <button
                  className="membership-social-btn google"
                  onClick={() => handleSocialLogin('google')}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  {t('membership.googleBtn')}
                </button>
                <button
                  className="membership-social-btn facebook"
                  onClick={() => handleSocialLogin('google')}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  {t('membership.facebookBtn')}
                </button>
                <button
                  className="membership-social-btn instagram"
                  onClick={() => handleSocialLogin('google')}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20"><path fill="url(#igGradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/><defs><linearGradient id="igGradient" x1="0" y1="0" x2="24" y2="24"><stop offset="0%" stopColor="#FFDC80"/><stop offset="25%" stopColor="#F77737"/><stop offset="50%" stopColor="#E1306C"/><stop offset="75%" stopColor="#C13584"/><stop offset="100%" stopColor="#833AB4"/></linearGradient></defs></svg>
                  {t('membership.instagramBtn')}
                </button>
              </div>

              <div className="membership-divider">
                <span>{t('membership.orContinueWith')}</span>
              </div>

              <form onSubmit={handleEmailRegister} className="membership-form">
                <div className="membership-field">
                  <label>{t('membership.nameLabel')} *</label>
                  <input
                    type="text"
                    placeholder={t('membership.namePlaceholder')}
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="membership-field">
                  <label>{t('membership.emailLabel')} *</label>
                  <input
                    type="email"
                    placeholder={t('membership.emailPlaceholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="membership-field">
                  <label>{t('membership.phoneLabel')}</label>
                  <input
                    type="tel"
                    placeholder={t('membership.phonePlaceholder')}
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>
                <div className="membership-field">
                  <label>{t('membership.addressLabel')}</label>
                  <input
                    type="text"
                    placeholder={t('membership.addressPlaceholder')}
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                  />
                </div>
                <div className="membership-field">
                  <label>{t('membership.companyLabel')}</label>
                  <input
                    type="text"
                    placeholder={t('membership.companyPlaceholder')}
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                  />
                </div>
                <div className="membership-field">
                  <label>{t('membership.howHeardLabel')}</label>
                  <input
                    type="text"
                    placeholder={t('membership.howHeardPlaceholder')}
                    value={howHeard}
                    onChange={e => setHowHeard(e.target.value)}
                  />
                </div>
                <div className="membership-field">
                  <label>Password *</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <button
                  type="submit"
                  className="membership-submit-btn"
                  disabled={registering}
                >
                  {registering ? 'Creating account...' : t('membership.registerBtn')}
                </button>
              </form>

              <p className="membership-login-link">
                {t('membership.alreadyAccount')}{' '}
                <Link to="/login">{t('membership.signIn')}</Link>
              </p>
            </div>
          </section>
        )}

        {step === 'success' && (
          <section className="membership-success">
            <div className="membership-success-card">
              <div className="membership-success-icon">🎉</div>
              <h2>{t('membership.successTitle')}</h2>
              <p>{t('membership.successDesc')}</p>
              <div className="membership-success-actions">
                <Link to="/quote" className="membership-cta-btn">
                  {t('membership.startQuote')}
                </Link>
                <Link to="/" className="membership-ghost-btn">
                  {t('membership.backHome')}
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default Membership;
